import React from "react";
import {
  Grid,
  TextField,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Typography,
} from "@mui/material";
import { ANDatePicker } from "@answersnow/pkg-theme";
import TimezonePicker from "elements/TimezonePicker";
import { COUNTRIES, ETHNICITIES, LANGUAGES, STATES } from "@constants";

interface EditFieldsProps {
  formValues: {
    firstName: string;
    preferredFirstName: string;
    lastName: string;
    dob: Date | null;
    gender: string;
    identifiedGender: string;
    transgender: boolean;
    pronouns: string;
    livesWith: string;
    address1: string;
    address2: string;
    state: string;
    city: string;
    zipCode: string;
    clientTimezone: string;
    country: string;
    ethnicity: string;
    primaryLanguage: string;
    needsInterpreter: boolean;
    preAuthRequired: boolean;
    emergencyContactName: string;
    emergencyContactPhoneNumber: string;
    emergencyContactRelationshipToClient: string;
    requiresTablet: boolean;
  };
  ethnicityList: string[];
  handleChange: (
    field: string
  ) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleChangeYesNo: (field: string) => (event: any) => void;
  handleEthnicityChange: (event: any) => void;
  handleDateChange: (field: string) => (value: Date | null) => void;
  setFormValues: React.Dispatch<React.SetStateAction<any>>;
}

export const EditFields: React.FC<EditFieldsProps> = ({
  formValues,
  ethnicityList,
  handleChange,
  handleChangeYesNo,
  handleEthnicityChange,
  handleDateChange,
  setFormValues,
}) => {
  return (
    <Grid container spacing={7}>
      <Grid item xs={12}>
        <TextField
          name="firstName"
          label="First name"
          onChange={handleChange("firstName")}
          value={formValues.firstName}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="preferredFirstName"
          label="Preferred first name"
          onChange={handleChange("preferredFirstName")}
          value={formValues.preferredFirstName}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="lastName"
          label="Last name"
          onChange={handleChange("lastName")}
          value={formValues.lastName}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <ANDatePicker
          id="dob"
          name="dob"
          label="Date of birth"
          value={formValues.dob}
          onChange={handleDateChange("dob")}
          format="PP"
          sx={{ width: "100%" }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="gender"
          label="Sex assigned at birth"
          select
          onChange={handleChange("gender")}
          value={formValues.gender}
          fullWidth
        >
          <MenuItem disabled value={""} />
          <MenuItem value="Female">Female</MenuItem>
          <MenuItem value="Male">Male</MenuItem>
          <MenuItem value="Non-binary">Non-binary</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="identifiedGender"
          label="Identified gender"
          select
          onChange={handleChange("identifiedGender")}
          value={formValues.identifiedGender}
          fullWidth
        >
          <MenuItem disabled value={""} />
          <MenuItem value="Female">Female</MenuItem>
          <MenuItem value="Male">Male</MenuItem>
          <MenuItem value="Non-binary">Non-binary</MenuItem>
          <MenuItem value="Gender Fluid">Gender Fluid</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="transgender"
          label="Transgender"
          select
          onChange={handleChangeYesNo("transgender")}
          value={formValues.transgender ? "Yes" : "No"}
          fullWidth
        >
          <MenuItem disabled value={""} />
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="pronouns"
          select
          label="Pronouns"
          onChange={handleChange("pronouns")}
          value={formValues.pronouns}
          fullWidth
        >
          <MenuItem value="" disabled />
          <MenuItem value="she/her">She/Her</MenuItem>
          <MenuItem value="he/him">He/Him</MenuItem>
          <MenuItem value="they/them">They/Them</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="livesWith"
          label="Client lives with"
          onChange={handleChange("livesWith")}
          value={formValues.livesWith}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="address1"
          label="Address"
          onChange={handleChange("address1")}
          value={formValues.address1}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="address2"
          label="Address 2"
          onChange={handleChange("address2")}
          value={formValues.address2}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="state"
          select
          label="State"
          onChange={handleChange("state")}
          value={formValues.state}
          fullWidth
          autoComplete="off"
        >
          <MenuItem disabled value={""}>
            Select a state
          </MenuItem>
          {Object.keys(STATES).map((key) => (
            <MenuItem key={key} value={key}>
              {STATES[key as keyof typeof STATES]}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="city"
          label="City"
          onChange={handleChange("city")}
          value={formValues.city}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="zipCode"
          label="ZIP code"
          value={formValues.zipCode}
          fullWidth
          inputProps={{ maxLength: 5 }}
          onChange={handleChange("zipCode")}
        />
      </Grid>
      <Grid item xs={12}>
        <TimezonePicker
          name="clientTimezone"
          timezone={formValues.clientTimezone}
          onTimezoneChange={handleChange("clientTimezone")}
          outlined
          fullWidth
          label="Time zone"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          select
          fullWidth
          label="Country"
          name="country"
          onChange={handleChange("country")}
          value={formValues.country}
        >
          <MenuItem disabled value={""}>
            Select
          </MenuItem>
          {Object.entries(COUNTRIES).map(([key, value]) => (
            <MenuItem key={key} value={key}>
              {value}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <FormControl className="select" sx={{ width: "100%" }}>
          <InputLabel>Race & ethnicity</InputLabel>
          <Select
            value={ethnicityList}
            onChange={handleEthnicityChange}
            input={<OutlinedInput label="Race & ethnicity" />}
            renderValue={(selected) => <Typography>{selected.join(", ")}</Typography>}
            multiple
            displayEmpty
          >
            {ETHNICITIES.map((key) => (
              <MenuItem key={key} value={key}>
                <Checkbox checked={ethnicityList.indexOf(key) > -1} />
                <ListItemText primary={key} primaryTypographyProps={{ variant: "body2" }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="primaryLanguage"
          label="Primary language"
          value={formValues.primaryLanguage}
          onChange={handleChange("primaryLanguage")}
          fullWidth
          select
        >
          <MenuItem disabled value={""} />
          {LANGUAGES.map((key) => (
            <MenuItem key={key} value={key}>
              {key}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="needsInterpreter"
          select
          label="Interpreter required"
          onChange={handleChangeYesNo("needsInterpreter")}
          value={formValues.needsInterpreter ? "Yes" : "No"}
          fullWidth
        >
          <MenuItem disabled value={""} />
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="emergencyContactName"
          label="Emergency contact"
          onChange={handleChange("emergencyContactName")}
          value={formValues.emergencyContactName}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="emergencyContactPhoneNumber"
          label="Emergency contact phone number"
          onChange={handleChange("emergencyContactPhoneNumber")}
          value={formValues.emergencyContactPhoneNumber}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="emergencyContactRelationshipToClient"
          label="Emergency contact relationship to client"
          onChange={handleChange("emergencyContactRelationshipToClient")}
          value={formValues.emergencyContactRelationshipToClient}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="preAuthRequired"
          label="Pre-authorization required"
          select
          onChange={handleChangeYesNo("preAuthRequired")}
          value={formValues.preAuthRequired ? "Yes" : "No"}
          fullWidth
        >
          <MenuItem disabled value={""} />
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              name="requiresTablet"
              onChange={(e) => setFormValues({ ...formValues, requiresTablet: e.target.checked })}
              checked={formValues.requiresTablet}
            />
          }
          label="Requires tablet"
        />
        <Typography
          component="p"
          variant="body2"
          color="text.secondary"
          marginLeft={8}
          marginTop={1}
        >
          The legal guardian(s) will be required to sign the tablet consent form if a tablet is
          required.
        </Typography>
      </Grid>
    </Grid>
  );
};

export default EditFields;
